import React, { useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Grid, Box, CssBaseline, Button, CircularProgress } from "@mui/material";

import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import ImagePreviewModal from "../../../../../common/ImagePreviewModal";
import AudioModal from "../../../../../common/AudioModal";
import ConfirmationModal from "../../../../../common/ConfirmationModal";
import { mainLayoutStyles, showNotification } from "../../../../../constants";
import {
  QTDetailsDataFieldsSection,
  QTDetailsOrganizationSection,
  QTDetailsInteractionsFieldsSection,
} from "../../components/components";
import {
  getSingleQuickTale,
  getSingleUser,
  updateQuickTale,
  uploadQuickTaleAudioToStorage,
  uploadQuickTaleImageToStorage,
  uploadProcessedAudioToStorage,
} from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import {
  createCustomNarrator,
  narratorNameExists,
  getCustomNarratorsByUser,
} from "../../../../../modules/OrganizationQuickTales/hooks/CustomNarratorsHooks";
import { resolveCreatedByData } from "../../../../../utils/createdByUtils";

import { useQuickTalesContext } from "../../../../../providers/OrganizationAdmin/QuickTalesProvider";

import { useOrganizationContext } from "../../../../../providers/OrganizationAdmin/OrganizationProvider";
import { Toast } from "../../../../../common/alert";
import { useAuth } from "../../../../../hooks";
import { GeoPoint } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../../../../firebase';
import { getUserContentReports } from "../../../GAContentModeration/hooks/ContentReportsHooks";
import Scrollbars from "react-custom-scrollbars-2";
import { ContentModerationTable } from "../../../GAContentModeration/components/components";
import moment from "moment";

const GAQuickTaleDetailsContainer = () => {
  const { currentUser } = useAuth();
  const {
    handleGetGAQuickTales,
    setLoading,
    loading,
    // insertQTAddSearchDocumentData,
  } = useQuickTalesContext();
  const { allOrganization, handleGetAllOrganizations } = useOrganizationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollbarsRef = useRef(null);
  const isSaving = useRef(false);
  const { id } = useParams();

  const [quicktaleDetails, setQuicktaleDetails] = useState(
    location?.state?.quicktaleDetails?.id
      ? location?.state?.quicktaleDetails
      : {}
  );
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  // const [quicktaleDetails, setQuicktaleDetails] = useState(
  //   location.state.quicktaleDetails
  // );
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [processedAudioModal, setProcessedAudioModal] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [uploadProcessedAudioFile, setUploadProcessedAudioFile] = useState(null);
  const [selected, setSelected] = useState([]);
  const [quickTaleReports, setQuickTaleReports] = useState([]);
  const [defaultTags, setDefaultTags] = useState([]);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const [narratorError, setNarratorError] = useState(false);
  const [convertingImage, setConvertingImage] = useState(false);

  //Add the below to the function below to add the original file name to the processed audio
  //ogFileName: quicktaleDetails.originalAudioName
  //need to update the cloud function as well

  // Helper: fire-and-forget call to processAudioWithAuphonicV5 (HTTP onRequest function)
  const fireAndForgetProcessAudio = async ({ fileUrl, qtTitle, ogFileName, collectionName, documentId }) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        console.error("No auth token available for background audio processing.");
        return;
      }
      const projectId = auth.app.options.projectId;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/processAudioWithAuphonicV5`;
      
      fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fileUrl,
          qtTitle: qtTitle || 'Audio Processing',
          ogFileName: ogFileName || 'audio',
          collectionName,
          documentId,
        }),
      }).catch(err => console.error('Background audio processing error:', err));
      
      console.log("Fire-and-forget audio processing initiated for:", documentId);
    } catch (err) {
      console.error("Error initiating background audio processing:", err);
    }
  };

  // Manual "Process Original Audio" button handler — fires V5 in the background
  const handleProcessOriginalAudio = async () => {
    if (!quicktaleDetails.audioUrl) {
      showNotification("No original audio file to process.");
      return;
    }
    showNotification("Audio processing started in the background.");
    await fireAndForgetProcessAudio({
      fileUrl: quicktaleDetails.audioUrl,
      qtTitle: quicktaleDetails.title,
      ogFileName: quicktaleDetails.originalAudioName,
      collectionName: 'quicktales',
      documentId: quicktaleDetails.id,
    });
  };

  // Convert current image (imageUrls[0]) to JPG format, regardless of original format.
  // This is done server-side via a Cloud Function to avoid browser CORS restrictions
  // that occur when trying to fetch/re-encode Firebase Storage images directly via
  // canvas from the client. The function shifts existing imageUrls down by one slot
  // and immediately persists to Firestore — independent of the page Save button,
  // matching the "Process Original Audio" fire-and-forget pattern.
  const handleConvertImageToJpg = async () => {
    const currentImageUrl = quicktaleDetails?.imageUrls?.[0] || quicktaleDetails?.photoUrl;
    if (!currentImageUrl) {
      showNotification("No image to convert.");
      return;
    }

    setConvertingImage(true);
    try {
      const functions = getFunctions();
      const convertQuickTaleImageToJpg = httpsCallable(functions, 'convertQuickTaleImageToJpg');

      const result = await convertQuickTaleImageToJpg({
        quicktaleId: quicktaleDetails.id,
        collection: 'quicktales',
      });

      const { url: newJpgUrl, imageUrls: newImageUrls } = result.data || {};

      if (!newJpgUrl) {
        throw new Error('Conversion did not return a new image URL.');
      }

      // Update local state so the UI reflects the new image right away.
      setQuicktaleDetails((prev) => ({
        ...prev,
        imageUrls: newImageUrls || [newJpgUrl],
        photoUrl: newJpgUrl,
      }));

      showNotification("Image converted to JPG successfully.");
    } catch (error) {
      console.error("Error converting image to JPG:", error);
      Toast("error", `Error converting image: ${error.message}`);
    } finally {
      setConvertingImage(false);
    }
  };

  const handleFileUpload = async (file, type, source = "upload") => {
    if (!file) return;

    setLoading(true);
    try {
      let dataTemp = {};

      if (type === "original") {
        const audioUrl = await uploadQuickTaleAudioToStorage(file.raw);
        dataTemp = {
          audioUrl: audioUrl,
          originalAudioName: file.raw.name,
        };
        
        // Create an audio element to get the duration
        const audio = new Audio(audioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration); // Duration in seconds
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration,
              // Clear old processed audio — it belongs to the previous original audio
              processedAudioUrl: '',
              processedAudioName: '',
            }));
            resolve();
          };
        });
        
        // Show notification instead of opening the process audio confirmation modal
        const toastMessage = source === "record"
          ? "Audio recorded successfully! Click Save to update the quicktale."
          : "Audio uploaded successfully! Click Save to update the quicktale.";
        showNotification(toastMessage);
      } else if (type === "processed") {
        const processedAudioUrl = await uploadProcessedAudioToStorage(file.raw);
        dataTemp = {
          processedAudioUrl: processedAudioUrl,
          processedAudioName: file.raw.name,
        };
        
        // Create an audio element to get the duration
        const audio = new Audio(processedAudioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration); // Duration in seconds
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration 
            }));
            resolve();
          };
        });
        
        showNotification(`Processed audio file uploaded successfully.`);
      }
    } catch (error) {
      Toast("error", `Error uploading file: ${error.message}`);
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleProcessedAudioModalOpen = () => {
    setProcessedAudioModal(true);
  };
  const handleProcessedAudioModalClose = () => {
    setProcessedAudioModal(false);
  };

  const handleCancelClick = () => {
    setCancelConfirmationModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    setCancelConfirmationModalOpen(false);
    setEditEnabled(false);
    // Reset any uploaded files
    setUploadImage(null);
    setUploadAudioFile(null);
    setUploadProcessedAudioFile(null);
    // Refresh the data to remove any unsaved changes
    await fetchQTDetails();
  };

  // Fetch all organizations on mount
  useEffect(() => {
    if (allOrganization && allOrganization.length > 0) {
      setOrganizationsData(allOrganization);
    } else {
      handleGetAllOrganizations();
    }
  }, []);

  // Update organizationsData when allOrganization changes
  useEffect(() => {
    if (allOrganization && allOrganization.length > 0) {
      setOrganizationsData(allOrganization);
    }
  }, [allOrganization]);

  // Set organization name and initialize approvalStatus with defaults if missing
  useEffect(() => {
    if (quicktaleDetails?.id) {
      // Find and set organization name
      if (quicktaleDetails.organization && organizationsData.length > 0) {
        const org = organizationsData.find(o => o.id === quicktaleDetails.organization);
        if (org) {
          setOrganizationName(org.name || "");
        } else {
          setOrganizationName("");
        }
      } else {
        setOrganizationName("");
      }

      // Initialize approvalStatus with default values if missing or incomplete
      if (!quicktaleDetails.approvalStatus || 
          !quicktaleDetails.approvalStatus.status || 
          !quicktaleDetails.approvalStatus.updatedAt || 
          !quicktaleDetails.approvalStatus.updatedBy) {
        setQuicktaleDetails(prev => ({
          ...prev,
          approvalStatus: {
            status: prev.approvalStatus?.status || "Pending",
            updatedAt: prev.approvalStatus?.updatedAt || new Date(),
            updatedBy: prev.approvalStatus?.updatedBy || currentUser?.id || "",
          }
        }));
      }
    }
  }, [quicktaleDetails?.id, quicktaleDetails?.organization, organizationsData, currentUser]);

  // Define fetchQTDetails before it's used in useEffect
  const fetchQTDetails = async () => {
    setIsLoadingData(true);
    try {
      // Use the ID from URL params directly
      const quicktaleId = id || location?.state?.quicktaleDetails?.id;
      
      if (!quicktaleId) {
        // No valid ID, redirect to list
        navigate('/quicktales', { replace: true });
        return;
      }

      const QTTempData = await getSingleQuickTale(quicktaleId);

      if (!QTTempData || !QTTempData.exists()) {
        // QuickTale not found, redirect to list
        navigate('/quicktales', { replace: true });
        return;
      }

      const createdById = QTTempData.data().createdBy;
      const narratorTemp = await getSingleUser(QTTempData.data().narrator);

      // Resolve "Created By": the stored id can point to a user OR an
      // organization. Shared helper tries users first, then organizations.
      // Pass the already-loaded org list so we can resolve orgs without a direct
      // organizations/{id} read. Result is tagged with _sourceType (display only).
      const resolvedCreatedByData = await resolveCreatedByData(
        createdById,
        allOrganization
      );



      let data = {
        ...QTTempData?.data(),
        id: QTTempData.id,
        photoUrl:
          QTTempData?.data().imageUrls?.[0] ??
          QTTempData?.data().photoUrl ??
          "",
        createdByData: resolvedCreatedByData,
        narratorData: narratorTemp?.data() ? narratorTemp.data() : null,
        // Initialize userNoProcess based on processedAudioUrl
        userNoProcess: QTTempData?.data().processedAudioUrl ? false : true,
        // userData: userTemp,
      };
      setDefaultTags(QTTempData.data().tags);
      setQuicktaleDetails(data);
    } catch (error) {
      console.error("Error fetching qt details:", error);
      // Redirect to list on error
      navigate('/quicktales', { replace: true });
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleGetQTReports = async () => {
    try {
      const quicktaleId = id || location?.state?.quicktaleDetails?.id;
      let querySnapshot = await getUserContentReports(quicktaleId);
      const reportsPromises = querySnapshot.docs.map(async (item) => {
        const userTemp = await getSingleUser(item.data().reportedBy);
        return {
          ...item.data(),
          id: item.id,
          reportedByData: userTemp?.data() ? userTemp.data() : {},
        };
      });

      const contentReports = await Promise.all(reportsPromises);

      console.log("reports:", contentReports);

      setQuickTaleReports(contentReports);
    } catch (error) {
      console.log("error getting user groups:", error.message);
    }
  };

  useEffect(() => {
    // Check if we have a valid ID from URL params
    if (id && id !== ":id") {
      fetchQTDetails();
      handleGetQTReports();
    } else if (location?.state?.quicktaleDetails?.id) {
      // Use data from navigation state
      setQuicktaleDetails(location.state.quicktaleDetails);
      handleGetQTReports();
    } else {
      // No valid ID - redirect to QuickTales list
      navigate('/quicktales', { replace: true });
    }
  }, [location.state, id, navigate]);

  // Show loading spinner while fetching data
  if (isLoadingData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CssBaseline />
        <GeneralAdminSidebar />
        <Grid container style={{ ...mainLayoutStyles, background: "#fff", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Grid>
      </Box>
    );
  }

  // Don't render details if we don't have valid quicktale data
  if (!quicktaleDetails?.id) {
    return null;
  }

  console.log("qt details::", quicktaleDetails);
  console.log("data::", quicktaleDetails);

  const handleUpdateQuickTale = async () => {
    if (!quicktaleDetails.narrator) {
      setNarratorError(true);
      showNotification("Told by is a required field.");
      return;
    } else {
      setNarratorError(false);
    }
    try {
      setLoading(true);
      
      // Check if narrator is a custom name (text) and not a user ID
      // If narratorData is null, it means they typed text or selected a custom narrator
      if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
        const narratorName = quicktaleDetails.narrator.trim();
        
        // Get user ID to check/create custom narrators
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        
        if (userIdToUse && narratorName) {
          try {
            // Fetch current custom narrators for this user
            const existingNarrators = await getCustomNarratorsByUser(userIdToUse);
            
            // Check if this narrator name already exists
            if (!narratorNameExists(narratorName, existingNarrators)) {
              // Create new custom narrator
              const newNarrator = await createCustomNarrator(narratorName, userIdToUse);
              if (newNarrator) {
                console.log("Created new custom narrator:", newNarrator);
              }
            }
          } catch (error) {
            console.error("Error checking/creating custom narrator:", error);
            // Continue with save even if custom narrator creation fails
          }
        }
      }
      
      console.log("quicktaleDetails before save:", quicktaleDetails);
      isSaving.current = true;
      let title =
        quicktaleDetails.title || `Untitled ${moment().format("MM-DD-YYYY")}`;
      setQuicktaleDetails({ ...quicktaleDetails, title: title });
      let dataTemp = {
        title: title,
        // title: quicktaleDetails.title || "",
        description: quicktaleDetails.description || "",
        status: quicktaleDetails.status || "",
        isDraft:
          quicktaleDetails?.isDraft !== undefined
            ? quicktaleDetails.isDraft
            : quicktaleDetails?.title?.includes("Untitled") || false,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || "",
        createdBy: quicktaleDetails.createdBy || "",
        narrator: quicktaleDetails.narrator || "",
        qtPriority:
          Number(
            quicktaleDetails.qtPriority ?? quicktaleDetails.orgPriority ?? 0
          ) || 0,
        location: quicktaleDetails.location || "",
        locationDisplay: quicktaleDetails.locationDisplay || "",
        locationCountry: quicktaleDetails.locationCountry || "",
        coordinates: new GeoPoint(
          quicktaleDetails?.coordinates?._lat || 0,
          quicktaleDetails?.coordinates?._long || 0
        ),
        g: {
          geohash: quicktaleDetails?.g?.geohash || "",
          geopoint: new GeoPoint(
            quicktaleDetails?.coordinates?._lat || 0,
            quicktaleDetails?.coordinates?._long || 0
          ),
        },
        // tags:
        //   quicktaleDetails.tags === defaultTags
        //     ? defaultTags
        //     : quicktaleDetails?.tags.split(","),
        // tags:
        //   quicktaleDetails.tags === location.state.quicktaleDetails.tags
        //     ? location.state.quicktaleDetails.tags
        //     : (quicktaleDetails.tags || "").split(","),
      };
      if (quicktaleDetails.tags !== defaultTags) {
        if (typeof quicktaleDetails.tags === "string") {
          dataTemp.tags = quicktaleDetails.tags.split(",");
        } else if (Array.isArray(quicktaleDetails.tags)) {
          dataTemp.tags = [...quicktaleDetails.tags];
        } else {
          dataTemp.tags = [];
        }
      }
      if (uploadImage != null) {
        let photoUrl = await uploadQuickTaleImageToStorage(uploadImage?.raw);
        // dataTemp.photoUrl = photoUrl;
        dataTemp.imageUrls = [photoUrl];
        dataTemp.originalPhotoName = uploadImage?.raw?.name;
      }
      if (quicktaleDetails.audioUrl) {
        dataTemp.audioUrl = quicktaleDetails.audioUrl;
      }
      if (quicktaleDetails.originalAudioName) {
        dataTemp.originalAudioName = quicktaleDetails.originalAudioName;
      }
      if (quicktaleDetails.processedAudioUrl) {
        dataTemp.processedAudioUrl = quicktaleDetails.processedAudioUrl;
      } else {
        dataTemp.processedAudioUrl = '';
      }
      if (quicktaleDetails.processedAudioName) {
        dataTemp.processedAudioName = quicktaleDetails.processedAudioName;
      } else {
        dataTemp.processedAudioName = '';
      }
      if (quicktaleDetails.audioLength) {
        dataTemp.audioLength = quicktaleDetails.audioLength;
      }
      // Save userNoProcess field
      if (quicktaleDetails.userNoProcess !== undefined) {
        dataTemp.userNoProcess = quicktaleDetails.userNoProcess;
      }
      // Save organization field (optional, can be null or empty string)
      if (quicktaleDetails.organization !== undefined) {
        dataTemp.organization = quicktaleDetails.organization || "";
      }
      // Save approvalStatus with defaults if not present
      // Status is stored lowercase — the mobile app is case-sensitive to lowercase values
      dataTemp.approvalStatus = {
        status: (quicktaleDetails.approvalStatus?.status || "Pending").toLowerCase(),
        updatedAt: quicktaleDetails.approvalStatus?.updatedAt || new Date(),
        updatedBy: quicktaleDetails.approvalStatus?.updatedBy || currentUser?.id || "",
      };
      console.log("data:", dataTemp);
      let responseTemp = await updateQuickTale(quicktaleDetails.id, dataTemp);
      
      await handleGetGAQuickTales();
      showNotification("QuickTale updated successfully.");
      
      // Update AddSearch index after successful Firestore update
      try {
        const functions = getFunctions();
        const updateQTInAddSearch = httpsCallable(functions, 'updateQTInAddSearch');
        
        const addSearchResult = await updateQTInAddSearch({
          docId: quicktaleDetails.id,
          data: {
            ...dataTemp,
            createdAt: quicktaleDetails.createdAt,
            creator: quicktaleDetails.createdBy,
            environment: process.env.REACT_APP_PROJECT_ENVIROMENT,
          },
          docType: "quicktale",
        });
        
        console.log("AddSearch update result:", addSearchResult.data);
      } catch (error) {
        console.error("Error updating QuickTale in AddSearch:", error);
      }
      
      // Re-index the image search vectors if the image was uploaded or edited.
      // This triggers for both:
      // 1. New image uploads (uploadImage set when user uploads a file)
      // 2. Image edits (uploadImage set when user edits existing image via crop modal)
      if (uploadImage != null) {
        const newImageUrl = dataTemp.imageUrls[0]; // New URL from Firebase Storage upload

        // (a) Google Vertex AI vision embeddings — the new image-search system.
        //     Re-embeds imageUrls[0] and stores the vector on the QT doc.
        try {
          console.log("Re-indexing QuickTale image to Vision. Image URL:", newImageUrl);
          const functions = getFunctions();
          const indexSingleQuickTaleToVision = httpsCallable(functions, 'indexSingleQuickTaleToVision');

          const visionResult = await indexSingleQuickTaleToVision({
            quicktaleId: quicktaleDetails.id,
            imageUrl: newImageUrl,
          });

          console.log("Vision re-index result:", visionResult.data);
        } catch (error) {
          console.error("Error re-indexing QuickTale to Vision:", error);
        }

        // (b) Clarifai — legacy image-search system, kept during the transition.
        //     Clarifai is being retired; this call must fail silently so its
        //     eventual shutdown never blocks a QuickTale save.
        try {
          console.log("Updating Clarifai with new/edited image. Image URL:", newImageUrl);
          const functions = getFunctions();
          const updateClarifaiInput = httpsCallable(functions, 'updateClarifaiInput');

          const clarifaiResult = await updateClarifaiInput({
            quicktaleId: quicktaleDetails.id,
            imageUrl: newImageUrl,
            metadata: {
              ORG_ID: quicktaleDetails.organization || "", // Can be empty - that's OK
              QTID: quicktaleDetails.id,
              description: quicktaleDetails.description || "",
              location: quicktaleDetails.location || "",
              locationDisplay: quicktaleDetails.locationDisplay || "",
              tags: Array.isArray(quicktaleDetails.tags) 
                ? quicktaleDetails.tags 
                : (typeof quicktaleDetails.tags === 'string' 
                  ? quicktaleDetails.tags.split(',') 
                  : []),
              title: title
            }
          });

          console.log("Clarifai update successful:", clarifaiResult.data);
        } catch (error) {
          // Fail silently — Clarifai is being retired and must never break the app.
          console.error("Error updating QuickTale in Clarifai (non-fatal, legacy):", error);
        }
      } else {
        console.log("Image re-index skipped - no image changes detected");
      }

      // Fire-and-forget: process audio in the background via V5
      // Triggers when: (a) there's an audioUrl but no processedAudioUrl, OR
      //                (b) new audio was uploaded/recorded in this edit session
      if (quicktaleDetails.audioUrl && (!quicktaleDetails.processedAudioUrl || uploadAudioFile != null)) {
        console.log("Initiating background audio processing via processAudioWithAuphonicV5");
        fireAndForgetProcessAudio({
          fileUrl: quicktaleDetails.audioUrl,
          qtTitle: title,
          ogFileName: quicktaleDetails.originalAudioName,
          collectionName: 'quicktales',
          documentId: quicktaleDetails.id,
        });
      }

      // Toast("success", "QuickTale updated successfully.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating quicktale", error.message);
    } finally {
      setLoading(false);
      setEditEnabled(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={quicktaleDetails && quicktaleDetails?.title}
        bgColor="#fff"
        buttonText={currentUser?.role === "moderator" ? undefined : "Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        onCancelClick={handleCancelClick}
        handleSave={() => handleUpdateQuickTale()}
        loading={loading}
        hideBackArrow={editEnabled}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/*Data card/container section*/}
          <QTDetailsDataFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            handleImagePreviewModalOpen={handleImagePreviewModalOpen}
            isSaving={isSaving}
            handleAudioModalOpen={handleAudioModalOpen}
            handleProcessedAudioModalOpen={handleProcessedAudioModalOpen}
            editEnabled={editEnabled}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            uploadProcessedAudioFile={uploadProcessedAudioFile}
            setUploadProcessedAudioFile={setUploadProcessedAudioFile}
            handleFileUpload={handleFileUpload}
            onProcessOriginalAudio={handleProcessOriginalAudio}
            onConvertImageToJpg={handleConvertImageToJpg}
            convertingImage={convertingImage}
            narratorError={narratorError}
            organizationsData={organizationsData}
            areasMode={"none"}
          />
          {/*organization card/container section*/}
          <QTDetailsOrganizationSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            editEnabled={editEnabled}
            organizationsData={organizationsData}
            organizationName={organizationName}
            currentUser={currentUser}
          />
          {/*interactions card/container section*/}
          <QTDetailsInteractionsFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
          />

          {quickTaleReports?.length ? (

              <ContentModerationTable
                selected={selected}
                setSelected={setSelected}
                contentReportsData={quickTaleReports}
                userDetailsPageReports={true}
                detailsPageReports={true}
                hideCheckbox={true}
              />
          ) : undefined}
        </Grid>
      </Grid>
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : quicktaleDetails?.photoUrl
        }
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : quicktaleDetails?.audioUrl
        }
      />
      <AudioModal
        modalOpen={processedAudioModal}
        handleClose={handleProcessedAudioModalClose}
        audioFile={
          uploadProcessedAudioFile != null && uploadProcessedAudioFile.preview
            ? uploadProcessedAudioFile.preview
            : quicktaleDetails?.processedAudioUrl
        }
      />
      <ConfirmationModal
        open={cancelConfirmationModalOpen}
        onClose={() => setCancelConfirmationModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Discard Changes"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
      />
    </Box>
  );
};

export default GAQuickTaleDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
const scrollbar = { width: "100%", height: "300px", marginLeft: "auto" };
const scrollbarprops = {
  width: "4px",
  backgroundColor: "#00A7CD",
  borderRadius: "2px",
};
